import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { createFreePreviewMember } from 'actions/LuxPlusActions'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalContext from 'contexts/ModalContext'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { isSameDay, dateDifference, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import * as Analytics from 'analytics/analytics'

function useJoinLuxPlusFreePreview(): {
  onPendingJoinFreePreview: () => void
  hasInviteExpiryDate: boolean
  formattedExpiryDate: string
  invitePillExpiryCopy: string
} {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { resolve } = useModalElementContext()
  const showModal = useContext(ModalContext)
  const isLoggedIn = useAppSelector(isLoggedInSelector)

  // Expiry DATE UI
  const freePreview = useAppSelector(state => state.luxPlus.products.freePreview)
  const inviteExpiryDate = freePreview?.offer?.expiryDate
  const hasInviteExpiryDate = !!inviteExpiryDate
  const intlDateFormatter = useIntlDateFormatter()
  const formattedExpiryDate = inviteExpiryDate ? intlDateFormatter(inviteExpiryDate, INTL_DMY_CASUAL_SHORT_FORMAT) : ''

  const onJoinFreePreview = useCallback(async() => {
    Analytics.trackClientEvent({
      subject: 'luxplus-free-preview-clicked',
      action: 'click',
      category: 'homepage-carousel',
      type: 'interaction',
      optimizelyEventId: '30321150380',
      optimizelyEventKey: 'luxplus-free-preview-clicked',
    })

    dispatch(createFreePreviewMember())
  }, [dispatch])

  // TODO engx task - refactor usePendingLoginHandler to use ModalContext
  const onPendingJoinFreePreview = useCallback(async() => {
    let loginResult: AccountAccessModalResult | undefined
    if (!isLoggedIn) {
      loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
    }
    if (loginResult?.loggedIn || isLoggedIn) {
      resolve()
      return onJoinFreePreview()
    }
  }, [isLoggedIn, onJoinFreePreview, resolve, showModal])

  const invitePillExpiryCopy = useMemo(() => {
    const inviteExpiresToday = isSameDay(new Date(), new Date(hasInviteExpiryDate ? inviteExpiryDate : ''))
    const inviteExpiryDayCount = dateDifference(new Date(hasInviteExpiryDate ? inviteExpiryDate : '')).days

    if (!hasInviteExpiryDate) {
      return intl.formatMessage({
        id: 'cyXT6kQdDmz_vRMRjsGL',
        defaultMessage: 'Limited-time offer',
        description: 'Pill text for LuxPlus free preview invite when no expiry date is available',
      })
    }

    if (inviteExpiresToday) {
      return intl.formatMessage({
        id: 'VvDe3KWxSmTkNuORoJYf',
        defaultMessage: 'Invite expires today',
        description: 'Pill text for LuxPlus free preview invite expiring today',
      })
    }

    return intl.formatMessage(
      {
        id: '5gxNykMBal2UU6g9hc2_',
        defaultMessage: '{dayCount, plural, one {Invite expires in # day} other {Invite expires in # days}}',
        description: 'Pill text for LuxPlus free preview invite with remaining days until expiry',
      },
      { dayCount: Math.max(inviteExpiryDayCount, 1) },
    )
  }, [hasInviteExpiryDate, inviteExpiryDate, intl])

  return {
    onPendingJoinFreePreview,
    hasInviteExpiryDate,
    formattedExpiryDate,
    invitePillExpiryCopy,
  }
}

export default useJoinLuxPlusFreePreview
